import {
  Menu,
  MenuItem,
  FormControlLabel,
  Divider,
  Grid2,
  IconButton,
  Tooltip,
  Checkbox,
  Collapse,
} from "@mui/material";
import MaterialBox from "../../../../components/MaterialBox";
import MaterialTypography from "../../../../components/MaterialTypography";
import { ExpandMore, FilterAltOff } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useSnackbar } from "../../../../context/popup";
import { fetchData } from "../../../../services";
import DropDownConfig from "../../../Utils/DropDownConfig";

const APIOperationsFilterDialog = ({
  anchorEl,
  open,
  onClose,
  filterData,
  setFilterData,
  onApply,
}) => {
  /* ================= STYLES ================= */

  const sectionHeader = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2px 6px",
    background: "#e9ecf5",
    borderRadius: "6px",
    cursor: "pointer",
    mb: 0.5,
  };

  const menuItemStyle = {
    borderRadius: "6px",
    my: 0.2,
    mx: 0.5,
    py: 0.3,
    "&:hover": { backgroundColor: "#dfe6ff" },
  };

  const labelStyle = {
    m: 0,
    "& .MuiFormControlLabel-label": { fontSize: "0.8rem" },
  };

  const { showSnackbar } = useSnackbar();

  /* ================= STATE ================= */

  const [openActive, setOpenActive] = useState(false);
  const [openApiRegistry, setOpenApiRegistry] = useState(false);
  const [openApiCategory, setOpenApiCategory] = useState(false);
  const [openApiEndpoint, setOpenApiEndpoint] = useState(false);
  const [openProtocol, setOpenProtocol] = useState(false);
  const [openHttpMethod, setOpenHttpMethod] = useState(false);
  const [openGraphqlType, setOpenGraphqlType] = useState(false);
  const [openRequestSchema, setOpenRequestSchema] = useState(false);
  const [openResponseSchema, setOpenResponseSchema] = useState(false);
  const [openNotificationSchema, setOpenNotificationSchema] = useState(false);

  const [apiRegistryOptions, setApiRegistryOptions] = useState([]);
  const [apiCategoryOptions, setApiCategoryOptions] = useState([]);
  const [apiEndpointOptions, setApiEndpointOptions] = useState([]);
  const [protocolOptions, setProtocolOptions] = useState([]);
  const [httpMethodOptions, setHttpMethodOptions] = useState([]);
  const [graphqlTypeOptions, setGraphqlTypeOptions] = useState([]);
  const [requestSchemaOptions, setRequestSchemaOptions] = useState([]);
  const [responseSchemaOptions, setResponseSchemaOptions] = useState([]);
  const [notificationSchemaOptions, setNotificationSchemaOptions] = useState([]);

  /* ================= FETCH DROPDOWNS ================= */

  useEffect(() => {
    const loadDropdowns = async () => {
      const results = await Promise.allSettled([
        fetchData(`/api-engine/api-registry?dropdown=true&isActive=true`),
        fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiApiCategory}`),
        fetchData(`/api-engine/api-endpoint?dropdown=true&isActive=true`),
        fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiProtocol}`),
        fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiHttpMethod}`),
        fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiGraphQLOperationType}`),
        fetchData(`/api-engine/schema?dropdown=true&isActive=true`),
        fetchData(`/api-engine/schema?dropdown=true&isActive=true`),
        fetchData(`/api-engine/schema?dropdown=true&isActive=true`),
      ]);

      const [
        apiRegistryRes,
        apiCategoryRes,
        apiEndpointRes,
        protocolRes,
        httpMethodRes,
        graphqlOpRes,
        reqSchemaRes,
        resSchemaRes,
        notSchemaRes,
      ] = results;

      if (apiRegistryRes.status === "fulfilled")
        setApiRegistryOptions(
          apiRegistryRes.value?.data?.items?.map((i) => ({
            label: i.name,
            value: i.id,
          })) || []
        );

      if (apiCategoryRes.status === "fulfilled")
        setApiCategoryOptions(
          apiCategoryRes.value?.data?.items?.map((i) => ({
            label: i.keyName,
            value: i.id,
          })) || []
        );

      if (apiEndpointRes.status === "fulfilled")
        setApiEndpointOptions(
          apiEndpointRes.value?.data?.items?.map((i) => ({
            label: i.endpointUrl,
            value: i.id,
          })) || []
        );

      if (protocolRes.status === "fulfilled")
        setProtocolOptions(
          protocolRes.value?.data?.items?.map((i) => ({
            label: i.keyName,
            value: i.id,
          })) || []
        );

      if (httpMethodRes.status === "fulfilled")
        setHttpMethodOptions(
          httpMethodRes.value?.data?.items?.map((i) => ({
            label: i.keyName,
            value: i.id,
          })) || []
        );

      if (graphqlOpRes.status === "fulfilled")
        setGraphqlTypeOptions(
          graphqlOpRes.value?.data?.items?.map((i) => ({
            label: i.keyName,
            value: i.id,
          })) || []
        );

      if (reqSchemaRes.status === "fulfilled")
        setRequestSchemaOptions(
          reqSchemaRes.value?.data?.items?.map((i) => ({
            label: i.name,
            value: i.id,
          })) || []
        );
      if (resSchemaRes.status === "fulfilled")
        setResponseSchemaOptions(
          resSchemaRes.value?.data?.items?.map((i) => ({
            label: i.name,
            value: i.id,
          })) || []
        );
      if (notSchemaRes.status === "fulfilled")
        setNotificationSchemaOptions(
          notSchemaRes.value?.data?.items?.map((i) => ({
            label: i.name,
            value: i.id,
          })) || []
        );
      // Optional warning if any API failed
      if (results.some((r) => r.status === "rejected")) {
        showSnackbar("Some dropdowns failed to load", "warning");
      }
    };
    if(open){
      loadDropdowns();
    }else{
      return
    }
  }, [open]);

  /* ================= HANDLERS ================= */

  const handleChange = ({ target: { name, value } }) => {
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onClearFilters = () => {
    setFilterData({
      isActive: "",
      apiRegistryId: "",
      apiCategoryId: "",
      apiEndpointId: "",
      protocolId: "",
      httpMethodId: "",
      graphqlOperationTypeId: "",
      requestSchemaId: "",
      responseSchemaId: "",
      notificationSchemaId: "",
    });
  };

  /* ================= UI ================= */

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      sx={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}
      PaperProps={{ elevation: 3, sx: { width: 280, p: 1.5 } }}
    >
      {/* ===== HEADER ===== */}
      <MaterialBox display="flex" justifyContent="space-between">
        <MaterialTypography variant="body3" fontWeight="bold">
          API Operation Filters
        </MaterialTypography>
        <Tooltip title="Clear Filters">
          <IconButton
            size="small"
            onClick={onClearFilters}
            sx={{ color: "#c00" }}
          >
            <FilterAltOff />
          </IconButton>
        </Tooltip>
      </MaterialBox>

      {/* ===== CONTENT ===== */}
      <MaterialBox mt={1} sx={{ maxHeight: 320, overflowY: "auto" }}>
        <Grid2 container>
          {/* ACTIVE */}
          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenActive(!openActive)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  Status
                </MaterialTypography>
                <ExpandMore
                  sx={{
                    transform: openActive ? "rotate(180deg)" : "rotate(0)",
                  }}
                />
              </MaterialBox>
              <Collapse in={openActive}>
                {[true, false].map((val) => (
                  <MenuItem
                    key={String(val)}
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({ target: { name: "isActive", value: val } })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox checked={filterData.isActive === val} />
                      }
                      label={val ? "Active" : "Inactive"}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>

          {/* GENERIC SECTIONS */}
          {[
            [
              "API Registry",
              openApiRegistry,
              setOpenApiRegistry,
              apiRegistryOptions,
              "apiRegistryId",
            ],
            [
              "API Category",
              openApiCategory,
              setOpenApiCategory,
              apiCategoryOptions,
              "apiCategoryId",
            ],
            [
              "API Endpoint",
              openApiEndpoint,
              setOpenApiEndpoint,
              apiEndpointOptions,
              "apiEndpointId",
            ],
            [
              "Protocol",
              openProtocol,
              setOpenProtocol,
              protocolOptions,
              "protocolId",
            ],
            [
              "HTTP Method",
              openHttpMethod,
              setOpenHttpMethod,
              httpMethodOptions,
              "httpMethodId",
            ],
            [
              "GraphQL Type",
              openGraphqlType,
              setOpenGraphqlType,
              graphqlTypeOptions,
              "graphqlOperationTypeId",
            ],
            [
              "Request Schema",
              openRequestSchema,
              setOpenRequestSchema,
              requestSchemaOptions,
              "requestSchemaId",
            ],
            [
              "Response Schema",
              openResponseSchema,
              setOpenResponseSchema,
              responseSchemaOptions,
              "responseSchemaId",
            ],
            [
              "Notification Schema",
              openNotificationSchema,
              setOpenNotificationSchema,
              notificationSchemaOptions,
              "notificationSchemaId",
            ],
          ].map(([title, openState, setOpen, options, field]) => (
            <Grid2 size={{ xs: 12 }} key={field}>
              <MaterialBox>
                <MaterialBox
                  sx={sectionHeader}
                  onClick={() => setOpen(!openState)}
                >
                  <MaterialTypography variant="body3" fontWeight="bold">
                    {title}
                  </MaterialTypography>
                  <ExpandMore
                    sx={{
                      transform: openState ? "rotate(180deg)" : "rotate(0)",
                    }}
                  />
                </MaterialBox>

                <Collapse in={openState}>
                  {options.map((item) => (
                    <MenuItem
                      key={item.value}
                      sx={menuItemStyle}
                      onClick={() =>
                        handleChange({
                          target: { name: field, value: item.value },
                        })
                      }
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterData[field] === item.value}
                          />
                        }
                        label={item.label}
                        sx={labelStyle}
                      />
                    </MenuItem>
                  ))}
                </Collapse>
              </MaterialBox>
            </Grid2>
          ))}
        </Grid2>
      </MaterialBox>

      <Divider sx={{ my: 1 }} />

      {/* ===== APPLY ===== */}
      <MenuItem
        onClick={onApply}
        sx={{
          mx: "auto",
          width: "fit-content",
          borderRadius: "6px",
          backgroundColor: "#bcc7e9",
          color: "white",
          "&:hover": { backgroundColor: "#93a6e8" },
        }}
      >
        <MaterialTypography variant="button" fontWeight="bold">
          APPLY FILTER
        </MaterialTypography>
      </MenuItem>
    </Menu>
  );
};

export default APIOperationsFilterDialog;
